import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, FileText, Download, CheckCircle2, Clock, Mail, Phone, MapPin, CalendarDays, GraduationCap } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Shell } from "@/components/Shell";
import PartnerOnboarding from "@/pages/PartnerOnboarding";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const FILTERS = [
  { key: "all", label: "Tous" },
  { key: "transmitted", label: "À contacter" },
  { key: "confirmed", label: "Confirmés" },
  { key: "enrolled", label: "Inscrits" },
];

const STATUS_META = {
  transmitted: { label: "À contacter", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  confirmed: { label: "Confirmé par l'élève", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  enrolled: { label: "Inscrit", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
};

export default function ProReservations() {
  const [partner, setPartner] = useState(null);
  const [ready, setReady] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [acting, setActing] = useState(false);
  const [finalizeOpen, setFinalizeOpen] = useState(false);

  const loadReservations = useCallback(() => {
    setLoading(true);
    api.get("/school/reservations")
      .then(({ data }) => {
        const list = data?.items || [];
        setItems(list);
        setSelected((prev) => (prev ? list.find((x) => x.id === prev.id) || null : null));
      })
      .catch((e) => toast.error(apiError(e.response?.data?.detail) || "Impossible de charger les dossiers."))
      .finally(() => setLoading(false));
  }, []);

  const loadOnboarding = useCallback(async () => {
    try {
      const ob = await api.get("/partner/onboarding");
      setPartner(ob.data.partner);
      setReady(true);
      if (ob.data.partner?.status && ob.data.partner.status !== "ACTIVE") return;
      loadReservations();
    } catch (e) {
      setReady(true);
      toast.error(apiError(e.response?.data?.detail) || e.message);
    }
  }, [loadReservations]);

  useEffect(() => { loadOnboarding(); }, [loadOnboarding]);

  const finalize = async (rid, body) => {
    setActing(true);
    try {
      const { data } = await api.post(`/school/reservations/${rid}/finalize`, body || {});
      toast.success(data?.commission != null ? `Inscription finalisée. Commission : ${data.commission} €.` : "Inscription finalisée.");
      setFinalizeOpen(false);
      await loadReservations();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setActing(false); }
  };

  const shown = items.filter((r) => filter === "all" || r.status === filter);
  const counts = items.reduce((acc, r) => { acc[r.status] = (acc[r.status] || 0) + 1; return acc; }, {});

  // Onboarding non terminé -> on affiche le parcours d'activation (comme avant).
  if (ready && partner && partner.status && partner.status !== "ACTIVE") {
    return (
      <Shell role="school">
        <div className="px-5 py-8 sm:px-10">
          <PartnerOnboarding partner={partner} onRefresh={loadOnboarding} />
        </div>
      </Shell>
    );
  }

  return (
    <Shell role="school">
      <div className="px-5 py-8 sm:px-10">
        <h1 className="text-3xl font-extrabold tracking-tighter sm:text-4xl">Dossiers élèves</h1>
        <p className="mt-2 text-neutral-500">
          {"Ces élèves ont choisi l'une de vos formules : ils vous sont automatiquement rattachés via le forfait sélectionné. C'est l'élève qui vous contacte pour finaliser son inscription. Une fois l'inscription réalisée de votre côté, cliquez sur « Inscription finalisée » pour le confirmer."}
        </p>

        <div className="mt-6 flex flex-wrap items-center gap-2">
          {FILTERS.map((f) => (
            <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`school-resa-filter-${f.key}`}
              className={`rounded-full px-3 py-1.5 text-xs font-bold ${filter === f.key ? "bg-brand text-white" : "bg-neutral-100 text-neutral-600"}`}>
              {f.label}{f.key === "all" ? ` (${items.length})` : counts[f.key] ? ` (${counts[f.key]})` : ""}
            </button>
          ))}
        </div>

        <div className="mt-5 grid gap-5 lg:grid-cols-[1fr_400px]">
          <div className="rounded-2xl border border-neutral-200 bg-white shadow-sm">
            {loading ? (
              <div className="grid place-items-center py-16"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
            ) : shown.length === 0 ? (
              <p className="p-8 text-center text-sm text-neutral-500" data-testid="school-resa-empty">{"Aucun dossier pour le moment. Dès qu'un élève choisit l'une de vos offres et que l'administrateur transmet son dossier, il apparaîtra ici."}</p>
            ) : (
              <ul className="divide-y divide-neutral-100">
                {shown.map((r) => {
                  const meta = STATUS_META[r.status] || { label: r.status, cls: "bg-neutral-100" };
                  return (
                    <li key={r.id}>
                      <button onClick={() => setSelected(r)} data-testid={`school-resa-row-${r.id}`}
                        className={`flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-neutral-50 ${selected?.id === r.id ? "bg-brand/5" : ""}`}>
                        <div className="min-w-0">
                          <p className="truncate font-bold text-neutral-900">{r.first_name} {r.last_name}</p>
                          <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-neutral-500">
                            <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {r.city}</span>
                            <span className="inline-flex items-center gap-1"><CalendarDays className="h-3 w-3" /> {r.month_label}</span>
                            <span>· {(r.documents || []).length} doc(s)</span>
                          </p>
                        </div>
                        <span className={`shrink-0 rounded-full border px-2.5 py-0.5 text-[11px] font-bold ${meta.cls}`}>{meta.label}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="rounded-2xl border border-neutral-200 bg-white p-5 shadow-sm" data-testid="school-resa-detail">
            {!selected ? (
              <p className="py-10 text-center text-sm text-neutral-400">Sélectionnez un dossier pour voir le détail.</p>
            ) : (
              <div>
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-black text-neutral-900">{selected.first_name} {selected.last_name}</h2>
                  <span className={`rounded-full border px-2.5 py-0.5 text-[11px] font-bold ${(STATUS_META[selected.status] || {}).cls || "bg-neutral-100"}`}>{(STATUS_META[selected.status] || {}).label || selected.status}</span>
                </div>
                <p className="mt-2 text-sm text-neutral-600"><strong>{selected.offer_title}</strong> — {selected.city} · {selected.month_label}{selected.forfait_price != null ? ` · ${selected.forfait_price} €` : ""}</p>
                <dl className="mt-3 space-y-1 text-sm">
                  <div className="flex items-center gap-2 text-neutral-600"><Mail className="h-4 w-4 text-brand" /> <a href={`mailto:${selected.email}`} className="hover:underline">{selected.email}</a></div>
                  <div className="flex items-center gap-2 text-neutral-600"><Phone className="h-4 w-4 text-brand" /> <a href={`tel:${selected.phone}`} className="hover:underline">{selected.phone}</a></div>
                  <div className="text-neutral-600">NEPH : <strong>{selected.has_neph ? (selected.neph_number || "oui") : selected.has_neph === false ? "non" : "—"}</strong> · Code : <strong>{selected.has_code === true ? "oui" : selected.has_code === false ? "non" : "—"}</strong></div>
                  {selected.already_took_exam === true && <div className="mt-1"><span className="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-bold text-amber-800" data-testid="resa-retake-badge">🔁 Repassage : a déjà passé l'examen pratique</span></div>}
                </dl>

                {selected.status === "transmitted" && (
                  <div className="mt-4 flex items-start gap-2 rounded-xl bg-amber-50 p-3 text-sm text-amber-800" data-testid="school-resa-contact-note">
                    <Clock className="mt-0.5 h-4 w-4 shrink-0" /> Cet élève va vous contacter pour finaliser son inscription. Vérifiez que son dossier est complet, puis confirmez l’inscription.
                  </div>
                )}

                <div className="mt-4">
                  <p className="text-xs font-bold uppercase tracking-wide text-neutral-400">Documents ({(selected.documents || []).length})</p>
                  <ul className="mt-2 space-y-2">
                    {(selected.documents || []).map((d) => (
                      <li key={d.id} className="flex items-center justify-between gap-2 rounded-lg bg-neutral-50 px-3 py-2 text-sm">
                        <span className="min-w-0 truncate text-neutral-700"><FileText className="mr-1 inline h-3.5 w-3.5 text-neutral-400" />{d.category === "code_result" ? "Relevé du code" : d.category} — {d.filename}</span>
                        <a href={`${BACKEND}/api/school/reservations/${selected.id}/documents/${d.id}`} target="_blank" rel="noreferrer"
                          className="inline-flex shrink-0 items-center gap-1 rounded-lg bg-brand/10 px-2.5 py-1 text-xs font-bold text-brand hover:bg-brand/20"><Download className="h-3.5 w-3.5" /> Ouvrir</a>
                      </li>
                    ))}
                    {(selected.documents || []).length === 0 && <li className="text-sm text-neutral-400">Aucun document déposé.</li>}
                  </ul>
                </div>

                <div className="mt-5">
                  {selected.status === "enrolled" ? (
                    <div className="inline-flex items-center gap-2 rounded-lg bg-emerald-50 px-4 py-2 text-sm font-bold text-emerald-700" data-testid="school-resa-enrolled">
                      <CheckCircle2 className="h-4 w-4" /> Inscription finalisée
                    </div>
                  ) : (
                    <button disabled={acting} onClick={() => setFinalizeOpen(true)} data-testid="school-resa-finalize"
                      className="inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2 text-sm font-bold text-white hover:bg-brand-hover disabled:opacity-50">
                      {acting ? <Loader2 className="h-4 w-4 animate-spin" /> : <GraduationCap className="h-4 w-4" />} Inscription finalisée
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {finalizeOpen && selected && (
        <FinalizeModal
          resa={selected}
          acting={acting}
          onClose={() => setFinalizeOpen(false)}
          onConfirm={(body) => finalize(selected.id, body)}
        />
      )}
    </Shell>
  );
}

function FinalizeModal({ resa, acting, onClose, onConfirm }) {
  const forfait = Number(resa.forfait_price) || 0;
  const [matches, setMatches] = useState(true);
  const [amount, setAmount] = useState("");

  const submit = () => {
    if (matches) { onConfirm({ amount_matches: true }); return; }
    const v = Number(amount);
    if (!v || v <= 0) { toast.error("Indique le montant réellement signé avec l'élève."); return; }
    onConfirm({ amount_matches: false, final_amount: v });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="school-finalize-modal">
        <h2 className="flex items-center gap-2 text-lg font-black text-neutral-900">
          <GraduationCap className="h-5 w-5 text-brand" /> Finaliser l’inscription
        </h2>
        <p className="mt-2 text-sm text-neutral-600">
          Élève <strong>{resa.first_name} {resa.last_name}</strong> — formule <strong>{resa.offer_title}</strong>
          {forfait ? <> affichée à <strong>{forfait} €</strong></> : null}.
        </p>
        <p className="mt-3 text-sm font-semibold text-neutral-800">Le montant signé avec l’élève correspond-il à la formule ?</p>
        <div className="mt-2 space-y-2">
          <label className="flex cursor-pointer items-start gap-2 rounded-lg border border-neutral-200 p-3 text-sm hover:border-brand">
            <input type="radio" checked={matches} onChange={() => setMatches(true)} data-testid="finalize-match-yes" className="mt-0.5 accent-brand" />
            <span>Oui, le montant signé correspond à la formule{forfait ? ` (${forfait} €)` : ""}.</span>
          </label>
          <label className="flex cursor-pointer items-start gap-2 rounded-lg border border-neutral-200 p-3 text-sm hover:border-brand">
            <input type="radio" checked={!matches} onChange={() => setMatches(false)} data-testid="finalize-match-no" className="mt-0.5 accent-brand" />
            <span>Non, j’ai signé un autre montant avec l’élève.</span>
          </label>
        </div>
        {!matches && (
          <div className="mt-3">
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-neutral-500">Montant final réellement signé (€)</label>
            <input type="number" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="finalize-final-amount"
              placeholder="Ex : 1450" className="w-full rounded-lg border border-neutral-300 px-3 py-2.5 text-sm outline-none focus:border-brand" />
            <p className="mt-1 text-xs text-amber-700">La commission PlanPermis sera calculée sur ce montant.</p>
          </div>
        )}
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-full border border-neutral-300 px-5 py-2.5 text-sm font-bold text-neutral-700 hover:border-neutral-400">Annuler</button>
          <button onClick={submit} disabled={acting} data-testid="finalize-confirm"
            className="inline-flex items-center gap-2 rounded-full bg-brand px-5 py-2.5 text-sm font-bold text-white hover:bg-brand-hover disabled:opacity-50">
            {acting ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Confirmer l’inscription
          </button>
        </div>
      </div>
    </div>
  );
}
